// @flow
import React, { Fragment, type ChildrenArray, type Element } from "react";
import { noop } from "lodash";
import type { NullaryFn } from "@tvg/types/Functional";
import Icon from "@fdr/static-ui/Icons";
import {
  info,
  warning,
  warningDiamond,
  hexagon,
  successOutline,
  cross
} from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import Button from "../../_atoms/Button";
import {
  Title,
  SubTextLink,
  InfoWrapper,
  ErrorWrapper,
  SuccessWrapper,
  TextWrapper,
  LogoContainer,
  TextContainer,
  ButtonsWrapper,
  WarningWrapper
} from "./styled-components";

type ButtonType =
  | "primary"
  | "secondary"
  | "betting"
  | "destructive"
  | "link"
  | "fanduel";
type Size = "big" | "small";
type MessageType =
  | "info"
  | "error"
  | "error-hexagon"
  | "success"
  | "warning"
  | "warning-diamond";
type PrimaryButton = {
  text: string,
  onClick?: NullaryFn<mixed>,
  url?: string
};
type SecondaryButton = {
  type: ButtonType,
  text: string,
  onClick?: NullaryFn<mixed>,
  url?: string
};

export type Props = {
  title: string,
  text: string,
  textSecondary?: string,
  subText: string,
  subTextLink?: string,
  size: Size,
  type: MessageType,
  logoAtStart?: boolean,
  primaryButton?: ?PrimaryButton,
  secondaryButton?: ?SecondaryButton,
  onClose?: NullaryFn<mixed>,
  borderLess?: boolean,
  children?:
    | Node
    | Element<*>
    | ChildrenArray<Node | Element<*> | boolean>
    | null
};

const messageTypesColors = {
  error: {
    key: "red",
    value: "default"
  },
  "error-hexagon": {
    key: "red",
    value: "default"
  },
  success: {
    key: "green",
    value: "800"
  },
  info: {
    key: "navy",
    value: "default"
  },
  warning: {
    key: "yellow",
    value: "900"
  },
  "warning-diamond": {
    key: "orange",
    value: "D2"
  }
};

const getButtons = (size, primaryButton, secondaryButton, borderLess) =>
  size === "big" &&
  primaryButton && (
    <ButtonsWrapper
      borderLess={borderLess}
      hasSecondaryButton={secondaryButton}
    >
      {
        <Button
          type="secondary"
          size="big"
          isInternalLink
          url={primaryButton.url}
          text={primaryButton.text}
          onClick={primaryButton.onClick}
        />
      }
      {secondaryButton && (
        <Button
          type={secondaryButton.type}
          size="big"
          isInternalLink
          url={secondaryButton.url}
          text={secondaryButton.text}
          onClick={secondaryButton.onClick}
        />
      )}
    </ButtonsWrapper>
  );

const getIconType = (type: MessageType) => {
  switch (type) {
    case "success":
      return successOutline;
    case "warning":
      return warning;
    case "warning-diamond":
      return warningDiamond;
    case "error":
      return warning;
    case "error-hexagon":
      return hexagon;
    default:
      return info;
  }
};

const Message = ({
  text,
  textSecondary,
  subText,
  subTextLink,
  size,
  type,
  logoAtStart,
  primaryButton,
  secondaryButton,
  onClose,
  borderLess,
  children,
  title
}: Props) => {
  const iconColor = () => {
    if (type === "info") return buildColor("blue", "D2");
    if (type === "error-hexagon") return buildColor("red", "D2");

    return buildColor(
      messageTypesColors[type].key,
      messageTypesColors[type].value
    );
  };

  const getContent = () => (
    <Fragment>
      <TextWrapper
        borderLess={borderLess}
        data-qa-label="messagesWrapper"
        size={size}
        hasSubText={!!subText}
        hasTitle={!!title}
      >
        <LogoContainer logoAtStart={logoAtStart}>
          <Icon
            icon={getIconType(type)}
            size={17}
            qaLabel="messagesIcon"
            color={iconColor()}
          />
        </LogoContainer>
        <TextContainer
          data-qa-label="messagesText"
          isCustomContainer={!!children}
          textColor={buildColor(
            messageTypesColors[type].key,
            messageTypesColors[type].value
          )}
        >
          {title && <Title>{title}</Title>}
          {children || (
            <Fragment>
              {
                // eslint-disable-next-line react/no-danger
                <span dangerouslySetInnerHTML={{ __html: text }} />
              }
              {!!textSecondary && (
                // eslint-disable-next-line react/no-danger
                <span dangerouslySetInnerHTML={{ __html: textSecondary }} />
              )}
              {!!subText && <span>{subText}</span>}
              {!!subTextLink && <SubTextLink>{subTextLink}</SubTextLink>}
            </Fragment>
          )}
        </TextContainer>
        {onClose !== noop && (
          <LogoContainer>
            <Icon
              icon={cross}
              size={24}
              qaLabel="closeIcon"
              color={buildColor(
                messageTypesColors[type].key,
                messageTypesColors[type].value
              )}
            />
          </LogoContainer>
        )}
      </TextWrapper>
      {getButtons(size, primaryButton, secondaryButton, borderLess)}
    </Fragment>
  );

  switch (type) {
    case "error":
      return (
        <ErrorWrapper borderLess={borderLess} onClick={onClose} size={size}>
          {getContent()}
        </ErrorWrapper>
      );
    case "error-hexagon":
      return (
        <ErrorWrapper borderLess={borderLess} onClick={onClose} size={size}>
          {getContent()}
        </ErrorWrapper>
      );
    case "success":
      return (
        <SuccessWrapper borderLess={borderLess} onClick={onClose} size={size}>
          {getContent()}
        </SuccessWrapper>
      );
    case "warning":
    case "warning-diamond":
      return (
        <WarningWrapper borderLess={borderLess} onClick={onClose} size={size}>
          {getContent()}
        </WarningWrapper>
      );
    default:
      return (
        <InfoWrapper borderLess={borderLess} onClick={onClose} size={size}>
          {getContent()}
        </InfoWrapper>
      );
  }
};

Message.defaultProps = {
  title: "",
  text: "",
  textSecondary: "",
  subText: "",
  size: "small",
  type: "info",
  logoAtStart: false,
  primaryButton: undefined,
  secondaryButton: undefined,
  onClose: noop,
  borderLess: false,
  children: null
};

export default Message;
